import { useHandler } from "@redotech/react-util/hook";
import UploadIcon from "@redotech/redo-icons/upload-01_filled.svg";
import { FlowType } from "@redotech/redo-model/return-flow";
import { SkuOption } from "@redotech/redo-model/return-flow/condition";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { SkuState } from "@redotech/redo-return-ui/flows/return-flow/condition-types/sku";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { UploadCsvModal } from "./upload-csv-modal";

export const SkuDetails = memo(function SkuDetails({
  state,
  setState,
  disabled,
  flowType,
}: ConditionTypeDetailsProps<SkuState>) {
  const [inputValue, setInputValue] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const stateRef = useRef(state);
  const setStateRef = useRef(setState);

  // Keep refs updated
  useEffect(() => {
    stateRef.current = state;
    setStateRef.current = setState;
  });

  const handleOpenUploadModal = useHandler(() => {
    setUploadModalOpen(true);
  });

  const handleCloseUploadModal = useHandler(() => {
    setUploadModalOpen(false);
  });

  const handleValuesUploaded = useHandler((newCodes: string[]) => {
    const currentState = stateRef.current;
    const existingCodes = currentState.codes || [];
    const uniqueCodes = [...new Set([...existingCodes, ...newCodes])];
    setStateRef.current({ ...currentState, codes: uniqueCodes });
  });

  const skuOptions: RedoListItem<SkuOption>[] = useMemo(
    () =>
      Object.values(SkuOption).map((option) => ({
        id: option,
        value: option,
        type: "text" as const,
        text: option,
      })),
    [],
  );

  const selectedOption = state.skuOption
    ? skuOptions.find((option) => option.value === state.skuOption)
    : undefined;

  const tags = useMemo(
    () => state.codes.map((code) => ({ text: code, color: "gray" as const })),
    [state.codes],
  );

  const description = `List of complete or partial SKU codes to check, case-sensitive${
    flowType === FlowType.FINALIZE_RETURN ||
    flowType === FlowType.FINALIZE_CLAIM
      ? ". If the SKU of any selected item matches, this condition will match."
      : ""
  }`;

  return (
    <>
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        flexGrow
        label="Match type"
        options={skuOptions}
        optionSelected={({ item }) =>
          setState({ ...state, skuOption: item.value })
        }
        placeholder="Select match type..."
        selectedItem={selectedOption}
        size="xs"
      />
      <RedoTagInputField
        delimiters={["newline"]}
        description={description}
        inputValue={inputValue}
        label="SKU codes (complete or partial)"
        placeholder="Type and press Enter to add"
        setInputValue={setInputValue}
        setTags={(codes) => setState({ ...state, codes })}
        size="sm"
        state={disabled ? "disabled" : "default"}
        tags={tags}
      />
      <Flex dir="column" gap="sm">
        <RedoButton
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={UploadIcon}
          onClick={handleOpenUploadModal}
          size="sm"
          type="button"
        >
          Upload CSV
        </RedoButton>
      </Flex>
      {uploadModalOpen && (
        <UploadCsvModal
          onClose={handleCloseUploadModal}
          onValuesUploaded={handleValuesUploaded}
          open={uploadModalOpen}
          possibleColumnNames={[
            "sku",
            "sku_code",
            "product_sku",
            "variant_sku",
            "code",
          ]}
          valueLabel="SKU codes"
        />
      )}
    </>
  );
});
